import { useEffect, useMemo, useRef, useState } from 'react';
import { IonContent, IonPage, IonSpinner } from '@ionic/react';
import { CalendarClock } from 'lucide-react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  loadStripe,
  type Appearance,
  type StripeElementLocale,
} from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { TopBar } from '../components/ui/TopBar';
import { useProfileStore } from '../store/useProfileStore';
import { useThemeStore } from '../store/useThemeStore';
import { useRootFontScale } from '../hooks/useRootFontScale';
import { useCancelOrder } from '../hooks/useOrders';
import { formatPickupMoment, formatTimeWindow } from '../utils/timeWindow';
import type { TimeWindow } from '../types/listing';
import type { Address } from '../types/address';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '', {
  developerTools: { assistant: { enabled: false } },
});

// Map a FE BCP-47 language tag to a StripeElementLocale literal. Stripe's
// supported-locale union is its own taxonomy (e.g. `es-419` for LatAm Spanish,
// no `es-MX`); when our tag doesn't have a one-to-one Stripe equivalent we
// fall through to `'auto'` and let Stripe pick from the browser.
function toStripeLocale(lang: string): StripeElementLocale {
  switch (lang) {
    case 'en':
      return 'en';
    case 'es-MX':
      return 'es-419';
    default:
      return 'auto';
  }
}

// The Stripe PaymentElement renders inside a cross-origin iframe on Stripe's
// domain, so our self-hosted `@fontsource/open-sans` (bundled on OUR origin) is
// invisible to it. Handing Stripe a public font stylesheet URL is the only way
// to get Open Sans painted inside the element — keep the weights in sync with
// the `fontFamily`/`fontWeight*` values below.
const STRIPE_FONT_SRC =
  'https://fonts.googleapis.com/css2?family=Open+Sans:wght@400;600;700&display=swap';

// Build the Stripe Elements `appearance` from our design tokens so the payment
// form matches the rest of checkout (warm-brown accent, Open Sans, 8px radius,
// bordered inputs like the `border border-border` cards around it). Stripe runs
// in an iframe and can't read our CSS variables, so every value is a concrete
// literal mirrored from src/theme/variables.scss + src/styles/global.scss. Keep
// them in sync if those tokens change.
//
// `scale` is the live root font-size multiplier (see useRootFontScale). The
// PaymentElement's iframe is cross-origin, so it never inherits our OS/dev
// text-size scaling the way the rem-based host page does — the only lever is
// Stripe's own font size. Stripe derives every text size in the element from
// `fontSizeBase`, so scaling that (and the px-literal spacing/padding in step)
// makes the whole form grow with the phone's text setting, already clamped to
// our 1.5× cap because `scale` reads the capped root size.
function buildStripeAppearance(isDark: boolean, scale: number): Appearance {
  const px = (base: number) => `${Math.round(base * scale * 100) / 100}px`;
  // --ion-color-primary / --ion-color-danger are identical across modes.
  const colorPrimary = '#8f4420';
  const colorDanger = '#B23A2E';

  // Page/input/text/border pulled from the light vs `.dark` token sets. Inputs
  // sit one step off the page background so the border reads in both modes.
  const colorText = isDark ? '#ffffff' : '#2f2f2f';
  const colorBackground = isDark ? '#393939' : '#ffffff';
  const colorTextPlaceholder = isDark ? '#a1a1a1' : '#979797';
  const borderColor = isDark ? '#474747' : '#e2e6ec';

  return {
    theme: isDark ? 'night' : 'stripe',
    variables: {
      colorPrimary,
      colorBackground,
      colorText,
      colorTextPlaceholder,
      colorDanger,
      fontFamily: '"Open Sans", sans-serif',
      borderRadius: '8px', // --radius: 0.5rem — fixed; only text scales
      spacingUnit: px(4),
      fontSizeBase: px(15),
    },
    rules: {
      '.Label': {
        fontWeight: '600',
        color: colorText,
      },
      '.Input': {
        border: `1px solid ${borderColor}`,
        boxShadow: 'none',
        padding: `${px(10)} ${px(12)}`,
      },
      '.Input:focus': {
        border: `1px solid ${colorPrimary}`,
        boxShadow: `0 0 0 1px ${colorPrimary}`,
        outline: 'none',
      },
      '.Input--invalid': {
        border: `1px solid ${colorDanger}`,
        boxShadow: 'none',
      },
      '.Tab': {
        border: `1px solid ${borderColor}`,
        boxShadow: 'none',
      },
      '.Tab:hover': {
        color: colorPrimary,
      },
      '.Tab--selected': {
        border: `1px solid ${colorPrimary}`,
        boxShadow: `0 0 0 1px ${colorPrimary}`,
        color: colorPrimary,
      },
      '.Tab--selected:focus': {
        boxShadow: `0 0 0 1px ${colorPrimary}`,
      },
      // "Save payment details" checkbox (shown via customerSessionClientSecret).
      // Stripe's default paints it with a drop shadow that clashes with our
      // flat, bordered language — strip the shadow and match the input border,
      // brown when checked.
      '.CheckboxInput': {
        boxShadow: 'none',
        border: `1px solid ${borderColor}`,
        backgroundColor: colorBackground,
      },
      '.CheckboxInput--checked': {
        boxShadow: 'none',
        backgroundColor: colorPrimary,
        border: `1px solid ${colorPrimary}`,
      },
      '.CheckboxInput:focus': {
        boxShadow: `0 0 0 1px ${colorPrimary}`,
      },
    },
  };
}

interface CheckoutNavState {
  clientSecret?: string;
  customerSessionClientSecret?: string;
  // ISO deadline of the order's hold (reserve time + hold window). Drives the
  // countdown; when it lapses the order's portion is released server-side and
  // we auto-abandon this checkout. Absent on a lost/refreshed session.
  expiresAt?: string;
  totalPaid?: number;
  orderNumber?: string;
  paymentId?: string;
  // Amount breakdown (cents) from the confirm response — rendered on the
  // summary block above the Stripe PaymentElement. Optional defensively in
  // case nav state is partial; UI degrades gracefully (Total falls back to
  // totalPaid, Tax/Fees rows hide when zero or absent).
  amountSubtotal?: number;
  amountTax?: number;
  amountFees?: number;
  amountTotal?: number;
  // The pickup the buyer chose, as a naive "YYYY-MM-DDTHH:mm:ss" wall-clock in
  // `pickupTimezone`. Carried purely so checkout can state the DAY: buyers were
  // paying for future-dated listings believing they were for today, and this
  // page used to show no scheduling information whatsoever. It's the last
  // screen before the money moves, so it's the last chance to catch it.
  pickupTime?: string;
  pickupTimezone?: string;
  /** "pickup" | "dropoff". A drop-off states its window instead of a time. */
  fulfillmentMethod?: string;
  dropoffWindow?: TimeWindow;
  /** What's being bought, restated so the payment screen says what it's for. */
  dishTitle?: string;
  quantity?: number;
  /** Drop-off only (cents). amountSubtotal includes it; split out for display. */
  dropoffFee?: number;
  /** Drop-off only: where the food is going. */
  deliveryAddress?: Address;
}

/**
 * Inner form that mounts the Stripe PaymentElement. Must live inside an
 * <Elements> provider so it can call `useStripe`/`useElements`.
 */
const CheckoutForm: React.FC<{
  orderId: string;
  expiresAt?: string;
  totalPaid?: number;
  orderNumber?: string;
  paymentId?: string;
  amountSubtotal?: number;
  amountTax?: number;
  amountFees?: number;
  amountTotal?: number;
  pickupTime?: string;
  pickupTimezone?: string;
  /** "pickup" | "dropoff". A drop-off states its window instead of a time. */
  fulfillmentMethod?: string;
  dropoffWindow?: TimeWindow;
  dishTitle?: string;
  quantity?: number;
  dropoffFee?: number;
  deliveryAddress?: Address;
}> = ({
  orderId,
  expiresAt,
  totalPaid,
  orderNumber,
  paymentId,
  amountSubtotal,
  amountTax,
  amountFees,
  amountTotal,
  pickupTime,
  pickupTimezone,
  fulfillmentMethod,
  dropoffWindow,
  dishTitle,
  quantity,
  dropoffFee,
  deliveryAddress,
}) => {
  const { t } = useTranslation('orders');
  const stripe = useStripe();
  const elements = useElements();
  const history = useHistory();
  const activeProfile = useProfileStore((s) => s.activeProfile);

  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // PaymentElement lifecycle gates for the Pay button. `paymentReady` flips on
  // the element's `onReady` (Stripe has mounted/painted the inputs);
  // `paymentComplete` tracks `onChange.complete` (all required fields filled
  // and valid). Until both are true the buyer literally can't produce a
  // chargeable payment, so the button stays locked with an explainer.
  const [paymentReady, setPaymentReady] = useState(false);
  const [paymentComplete, setPaymentComplete] = useState(false);

  // Cancel-on-abandon: history.listen + beforeunload, NOT useEffect cleanup —
  // StrictMode would rehearse the cleanup at mount and cancel the order
  // before the buyer could pay.
  const submittedRef = useRef(false);
  const canceledRef = useRef(false);

  // Goes through the mutation rather than the `cancelOrder` service function so
  // the release actually invalidates the listing/feed caches. The direct
  // service call this replaced skipped all of that, which is why bailing out of
  // checkout left the detail page still showing the portion as taken.
  //
  // `mutate` (not `mutateAsync`) is deliberate: we fire this while navigating
  // away, so there is nobody left to await it, and `mutate` swallows rejection
  // internally. The hook-level `onSuccess` invalidation still runs after this
  // component unmounts — a pending mutation is kept alive when its last
  // observer leaves, and only the per-call callbacks are dropped. That is what
  // makes the listing page's refetch fire against post-release state rather
  // than racing the DELETE.
  const cancelMutation = useCancelOrder();
  const cancelRef = useRef(cancelMutation);
  cancelRef.current = cancelMutation;

  useEffect(() => {
    const cancel = (keepalive: boolean) => {
      if (submittedRef.current || canceledRef.current) return;
      canceledRef.current = true;
      // A failed release isn't actionable by a user who has already left the
      // page; the server's expiry worker reclaims the hold either way.
      cancelRef.current.mutate({ id: orderId, keepalive });
    };

    const unlisten = history.listen((next) => {
      if (next.pathname.startsWith(`/orders/${orderId}/checkout`)) return;
      cancel(false);
    });

    const handleBeforeUnload = () => cancel(true);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      unlisten();
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [history, orderId]);

  // Hold countdown. `expiresAt` is the server's held_until (reserve + hold
  // window); once it lapses the backend releases the portion (the expired-hold
  // worker abandons the order), so a buyer who paid a moment later would be
  // paying into a dead hold. At zero we auto-abandon and bounce to the order
  // page — unless a payment is already in flight (submittedRef), in which case
  // we let Stripe resolve rather than cancel mid-authorization. Null when the
  // session was lost/refreshed (no nav state), which hides the timer.
  const [remainingMs, setRemainingMs] = useState<number | null>(() =>
    expiresAt ? Math.max(0, new Date(expiresAt).getTime() - Date.now()) : null,
  );

  useEffect(() => {
    if (!expiresAt) return;
    const deadline = new Date(expiresAt).getTime();
    const tick = () => {
      const left = deadline - Date.now();
      setRemainingMs(Math.max(0, left));
      if (left > 0) return;
      // Hold lapsed. Don't touch an in-flight payment; the leave/cancel guards
      // also prevent a double-abandon with the history.listen handler.
      if (submittedRef.current || canceledRef.current) return;
      canceledRef.current = true;
      cancelRef.current.mutate({ id: orderId });
      history.replace(`/orders/${orderId}`);
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [expiresAt, orderId, history]);

  const profileAddress = activeProfile?.metadata?.addresses?.find(
    (a) => a.type === 'profile',
  );

  // The Pay button is only live once Stripe + Elements have loaded, the
  // PaymentElement has mounted, the buyer has filled in valid details, and no
  // request is already in flight.
  const payDisabled =
    submitting || !stripe || !elements || !paymentReady || !paymentComplete;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (payDisabled || !stripe || !elements) return;

    setError('');
    setSubmitting(true);
    submittedRef.current = true;

    try {
      const { error: stripeError } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
        confirmParams: {
          return_url: `${window.location.origin}/orders/${orderId}`,
        },
      });

      if (stripeError) {
        // Payment didn't go through — re-arm cancel-on-abandon so the buyer
        // can still back out and have the order cleaned up.
        submittedRef.current = false;
        // Stripe-provided message wins when present (it's locale-aware via
        // the Stripe SDK's own locale param); our string is the fallback
        // for the rare error without a `message`.
        setError(stripeError.message || t('checkout.errorPaymentFailed'));
        setSubmitting(false);
        return;
      }

      // No redirect required (common case for test cards) — navigate ourselves.
      history.replace(`/orders/${orderId}`, { paymentId });
    } catch {
      submittedRef.current = false;
      setError(t('checkout.errorGeneric'));
      setSubmitting(false);
    }
  };

  // Resolved in the LISTING's zone (carried alongside the wall-clock), so the
  // day named here is the day the buyer has to show up where the food is.
  const pickupMoment = formatPickupMoment(pickupTime, pickupTimezone);
  const isDropoff = fulfillmentMethod === 'dropoff';

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      {remainingMs != null && (
        <div
          className={`rounded-xl border p-3 text-center text-sm ${
            remainingMs <= 60_000
              ? 'border-brand-danger/40 bg-brand-danger/10 text-brand-danger'
              : 'border-border bg-muted/30 text-muted-foreground'
          }`}
        >
          {t('checkout.holdTimer', {
            time: `${Math.floor(remainingMs / 60_000)}:${String(
              Math.floor((remainingMs % 60_000) / 1000),
            ).padStart(2, '0')}`,
          })}
        </div>
      )}
      {/* When the pickup is. Above the amounts on purpose: the day is the thing
          buyers were getting wrong, and this is the last screen before the
          charge. Stated whether or not it's today — if only future dates
          carried a date, its absence would be the signal, which is exactly the
          kind of thing people miss. Future dates additionally get the tint and
          the days-away line. The whole block hides on a lost/refreshed session,
          like every other nav-state-fed field here. */}
      {pickupMoment && (
        <div
          className={`hp-pickup-alert ${
            pickupMoment.isToday ? 'hp-pickup-alert--plain' : ''
          }`}
        >
          <CalendarClock className="hp-pickup-alert__icon h-5 w-5" aria-hidden="true" />
          <div className="min-w-0">
            <p className="text-xs text-muted-foreground">
              {t(isDropoff ? 'checkout.summary.dropoff' : 'checkout.summary.pickup')}
            </p>
            <p className="text-base font-semibold">
              {isDropoff && dropoffWindow?.start ? formatTimeWindow(dropoffWindow) : pickupMoment.full}
            </p>
            {!pickupMoment.isToday && (
              <p className="mt-0.5">
                {t('checkout.pickupNotToday', {
                  day: pickupMoment.dayLabel,
                  count: Math.max(pickupMoment.daysAway, 1),
                })}
              </p>
            )}
          </div>
        </div>
      )}

      {isDropoff && deliveryAddress && (
        <div className="rounded-xl border border-border p-4 text-sm space-y-0.5">
          <p className="text-xs text-muted-foreground">{t('inProgress.deliveryAddressLabel')}</p>
          <p className="font-medium">
            {[deliveryAddress.street1 || deliveryAddress.address, deliveryAddress.street2]
              .filter(Boolean)
              .join(', ')}
          </p>
          <p className="text-muted-foreground">
            {[deliveryAddress.city, deliveryAddress.stateProvince, deliveryAddress.postalCode]
              .filter(Boolean)
              .join(', ')}
          </p>
        </div>
      )}

      <div className="rounded-xl border border-border bg-muted/30 p-4 text-sm space-y-1">
        {dishTitle && (
          <div className="flex justify-between gap-3 font-medium">
            <span className="min-w-0 truncate">{dishTitle}</span>
            {quantity != null && <span className="shrink-0">×{quantity}</span>}
          </div>
        )}
        {orderNumber && (
          <div className="flex justify-between">
            <span className="text-muted-foreground">{t('checkout.summary.order')}</span>
            <span>{orderNumber}</span>
          </div>
        )}
        {/* A drop-off's subtotal already includes the delivery fee; show the
            two apart, matching the order sheet the buyer just confirmed. */}
        {amountSubtotal != null && isDropoff && !!dropoffFee ? (
          <>
            <div className="flex justify-between text-muted-foreground">
              <span>{t('checkout.summary.food')}</span>
              <span>${((amountSubtotal - dropoffFee) / 100).toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-muted-foreground">
              <span>{t('checkout.summary.delivery')}</span>
              <span>${(dropoffFee / 100).toFixed(2)}</span>
            </div>
          </>
        ) : amountSubtotal != null ? (
          <div className="flex justify-between text-muted-foreground">
            <span>{t('checkout.summary.subtotal')}</span>
            <span>${(amountSubtotal / 100).toFixed(2)}</span>
          </div>
        ) : null}
        {amountTax != null && amountTax > 0 && (
          <div className="flex justify-between text-muted-foreground">
            <span>{t('checkout.summary.tax')}</span>
            <span>${(amountTax / 100).toFixed(2)}</span>
          </div>
        )}
        {amountFees != null && amountFees > 0 && (
          <div className="flex justify-between text-muted-foreground">
            <span>{t('checkout.summary.fees')}</span>
            <span>${(amountFees / 100).toFixed(2)}</span>
          </div>
        )}
        {(amountTotal ?? totalPaid) != null && (
          <div className="flex justify-between font-semibold pt-1 border-t border-border/50">
            <span>{t('checkout.summary.total')}</span>
            <span>${((amountTotal ?? totalPaid)! / 100).toFixed(2)}</span>
          </div>
        )}
      </div>

      <PaymentElement
        onReady={() => setPaymentReady(true)}
        onChange={(e) => setPaymentComplete(e.complete)}
        options={{
          layout: 'tabs',
          defaultValues: {
            billingDetails: {
              address: {
                postal_code: profileAddress?.postalCode || '',
                country: profileAddress?.country || '',
              },
            },
          },
        }}
      />

      {error && <p className="text-sm text-brand-danger text-center">{error}</p>}

      {/* Explain why the Pay button is locked. Suppressed while submitting (the
          button shows its own spinner) and once the payment is actually
          chargeable (button enabled — no explainer needed). */}
      {!submitting && payDisabled && (
        <p className="text-sm text-muted-foreground text-center">
          {stripe && elements && paymentReady
            ? t('checkout.lockedIncomplete')
            : t('checkout.lockedLoading')}
        </p>
      )}

      <button
        type="submit"
        disabled={payDisabled}
        className="flex w-full items-center justify-center gap-2 rounded-xl bg-brand-primary py-3 text-base font-semibold text-white active:opacity-80 disabled:opacity-50"
      >
        {submitting ? (
          <>
            <IonSpinner name="crescent" className="h-5 w-5" />
            {t('checkout.processing')}
          </>
        ) : (amountTotal ?? totalPaid) != null ? (
          t('checkout.payCtaWithAmount', { amount: `$${((amountTotal ?? totalPaid)! / 100).toFixed(2)}` })
        ) : (
          t('checkout.payCta')
        )}
      </button>
    </form>
  );
};

export const OrderCheckoutPage: React.FC = () => {
  // `i18n.language` feeds the Stripe Elements `locale` option below so the
  // Payment Element's labels / inline errors match the user's chosen app
  // language (not just their browser language — Stripe's default `'auto'`
  // sniffs browser, missing in-app overrides).
  const { t, i18n } = useTranslation('orders');
  // Drives the Stripe `appearance` theme (night vs stripe) and re-themes the
  // Payment Element live when the user flips modes — see the useMemo deps.
  const isDark = useThemeStore((s) => s.isDark);
  // Live root font-size multiplier (OS/dev text-size, capped at 1.5×). Feeds the
  // Stripe appearance so the PaymentElement — in its own cross-origin iframe —
  // tracks the phone's text size like the rem-based host page does.
  const textScale = useRootFontScale();
  const { id } = useParams<{ id: string }>();
  const location = useLocation<CheckoutNavState | undefined>();
  const {
    clientSecret,
    customerSessionClientSecret,
    expiresAt,
    totalPaid,
    orderNumber,
    paymentId,
    amountSubtotal,
    amountTax,
    amountFees,
    amountTotal,
    pickupTime,
    pickupTimezone,
    fulfillmentMethod,
    dropoffWindow,
    dishTitle,
    quantity,
    dropoffFee,
    deliveryAddress,
  } = location.state ?? {};

  // Stripe Elements options. `clientSecret` keys the Stripe session and only
  // the initial value matters; `locale` does react to changes — if the user
  // flips lang mid-checkout the Elements provider remounts in the new lang
  // (Stripe's translation isn't hot-swappable).
  //
  // `i18n.language` is the canonical "what UI lang am I in" but its BCP-47
  // tags don't 1:1 map to StripeElementLocale's union: Stripe ships `es-419`
  // (Latin-American Spanish) but not `es-MX`, so we route MX → 419 and let
  // any other unknown tag fall through to `'auto'` (Stripe picks based on
  // the browser).
  const elementsOptions = useMemo(
    () => {
      if (!clientSecret) return null;
      const locale = toStripeLocale(i18n.language);
      const base = {
        clientSecret,
        locale,
        appearance: buildStripeAppearance(isDark, textScale),
        fonts: [{ cssSrc: STRIPE_FONT_SRC }],
      };
      return customerSessionClientSecret
        ? { ...base, customerSessionClientSecret }
        : base;
    },
    [clientSecret, customerSessionClientSecret, i18n.language, isDark, textScale],
  );

  return (
    <IonPage>
      <TopBar />
      <IonContent>
        <div className="mx-auto w-full max-w-md p-4 space-y-4">
          <h1 className="text-xl font-bold">{t('checkout.title')}</h1>

          {!clientSecret && (
            <div className="rounded-xl border border-border bg-muted/30 p-4 text-sm space-y-2">
              <p className="font-medium">{t('checkout.lostSession.title')}</p>
              <p className="text-muted-foreground">
                {t('checkout.lostSession.body')}
              </p>
            </div>
          )}

          {clientSecret && elementsOptions && (
            <Elements stripe={stripePromise} options={elementsOptions}>
              <CheckoutForm
                orderId={id}
                expiresAt={expiresAt}
                totalPaid={totalPaid}
                orderNumber={orderNumber}
                paymentId={paymentId}
                amountSubtotal={amountSubtotal}
                amountTax={amountTax}
                amountFees={amountFees}
                amountTotal={amountTotal}
                pickupTime={pickupTime}
                pickupTimezone={pickupTimezone}
                fulfillmentMethod={fulfillmentMethod}
                dropoffWindow={dropoffWindow}
                dishTitle={dishTitle}
                quantity={quantity}
                dropoffFee={dropoffFee}
                deliveryAddress={deliveryAddress}
              />
            </Elements>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
};
